import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { fetchNotificationsPage } from "../../../../utils/apiUtils";
import {
    NOTIFICATIONS_QUERY_KEY_ROOT,
    QueueFilter,
    SendType,
} from "./constants";
import { useFilterSearchParams } from "./useFilterSearchParams";
import { useTotalCount } from "./useTotalCount";

/**
 * Public API returned by {@link useNotifications} and passed to queue layout children as `useNotificationsHookData`.
 *
 * @typedef {object} UseNotificationsProps
 * @property {string} sendType Active send-type tab / API filter ({@link SendType}).
 * @property {import('react').Dispatch<import('react').SetStateAction<string>>} setSendType Updates send type (also synced by {@link useFilterSearchParams}).
 * @property {string | null} queueFilter All / Sent / Errors segment ({@link QueueFilter}; `null` is ALL).
 * @property {import('react').Dispatch<import('react').SetStateAction<string | null>>} setQueueFilter Updates queue segment (also synced by {@link useFilterSearchParams}).
 * @property {Record<string, string>} columnFilters Non-empty column filters (keys match `NOTIFICATION_COLUMN_FILTERS` in `./constants`).
 * @property {import('react').Dispatch<import('react').SetStateAction<Record<string, string>>>} setColumnFilters Replaces column filters (also synced by {@link useFilterSearchParams}).
 * @property {Record<string, unknown>[]} notifications Flattened rows from all loaded infinite-query pages.
 * @property {number} notificationCount `notifications.length` (loaded rows only; useful for debugging / edge UI).
 * @property {number | undefined} notificationsTotalCount From {@link useTotalCount} — total rows for filters (`GET /notifications/count`).
 * @property {boolean} isNotificationsTotalCountLoading From {@link useTotalCount}.
 * @property {boolean} isNotificationsTotalCountError From {@link useTotalCount} — latest count request failed.
 * @property {() => void} resetFilters Drops cached notification queries and resets filters to defaults.
 * @property {() => void} refreshNotificationsList Refetches the current list and count query keys (filters unchanged).
 * @property {(options?: import('@tanstack/react-query').FetchNextPageOptions) => Promise<import('@tanstack/react-query').InfiniteQueryObserverResult>} fetchNextPage Loads the next notifications page for the current filters.
 * @property {boolean} hasNextPage Whether another page exists per the last response cursor.
 * @property {boolean} isFetchingNextPage True while a "load more" request is in flight.
 * @property {boolean} isLoading True on initial load for this query key (no cached data yet).
 * @property {boolean} isError True when the latest fetch for this list query failed.
 */

/**
 * State and TanStack `useInfiniteQuery` wiring for the Notifications queue, plus filter totals via {@link useTotalCount}.
 *
 * {@link useFilterSearchParams} keeps filters aligned with the URL. `resetFilters` clears all queries under
 * {@link NOTIFICATIONS_QUERY_KEY_ROOT}; `refreshNotificationsList` refetches the current list and count keys.
 *
 * @returns {UseNotificationsProps} Queue list state, filter setters, and query controls.
 */
export function useNotifications() {
    const queryClient = useQueryClient();

    const {
        sendType,
        setSendType,
        queueFilter,
        setQueueFilter,
        columnFilters,
        setColumnFilters,
    } = useFilterSearchParams();

    const {
        notificationsCountQueryKey,
        notificationsTotalCount,
        isNotificationsTotalCountLoading,
        isNotificationsTotalCountError,
    } = useTotalCount({ sendType, queueFilter, columnFilters });

    const notificationsQueryKey = useMemo(
        () => [
            NOTIFICATIONS_QUERY_KEY_ROOT,
            sendType,
            queueFilter,
            columnFilters,
        ],
        [sendType, queueFilter, columnFilters],
    );

    const query = useInfiniteQuery({
        queryKey: notificationsQueryKey,
        queryFn: ({ pageParam }) =>
            fetchNotificationsPage({
                sendType,
                queueFilter,
                columnFilters,
                cursor: pageParam ?? undefined,
            }),
        initialPageParam: undefined,
        getNextPageParam: (lastPage) =>
            lastPage?.nextCursor ? lastPage.nextCursor : undefined,
    });

    const notifications =
        query.data?.pages.flatMap((page) => page.notifications ?? []) ?? [];

    // Clears the data, resets the filters, and fetches the 1st page again
    const resetFilters = useCallback(() => {
        queryClient.removeQueries({
            queryKey: [NOTIFICATIONS_QUERY_KEY_ROOT],
            exact: false,
        });

        setSendType(SendType.AUTOMATED);
        setQueueFilter(QueueFilter.ALL);
        setColumnFilters({});
    }, [queryClient, setSendType, setQueueFilter, setColumnFilters]);

    // Same as resetting but does not clear the filters. Also resets the total count.
    const refreshNotificationsList = useCallback(() => {
        queryClient.resetQueries({
            queryKey: notificationsQueryKey,
            exact: true,
        });
        queryClient.resetQueries({
            queryKey: notificationsCountQueryKey,
            exact: true,
        });
    }, [queryClient, notificationsQueryKey, notificationsCountQueryKey]);

    const {
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
    } = query;

    return {
        sendType,
        setSendType,
        queueFilter,
        setQueueFilter,
        columnFilters,
        setColumnFilters,
        notifications,
        notificationCount: notifications.length,
        notificationsTotalCount,
        isNotificationsTotalCountLoading,
        isNotificationsTotalCountError,
        resetFilters,
        refreshNotificationsList,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
    };
}
